<template>
  <div class="login-container">
    <div class="login-container_card">
      <div class="login-card">
        <!-- Indicador de depuración para mostrar si estamos en modo móvil -->
        <!-- <div v-if="showDebug" class="debug-mode">
          <p>Modo: {{ isMobile ? 'Móvil' : 'Escritorio' }}</p>
          <p>Imagen: {{ isMobile ? 'Versión móvil' : 'Versión escritorio' }}</p>
          <p>Ancho: {{ windowWidth }}px</p>
        </div> -->

        <!-- Implementación corregida de picture para móviles -->
        <img v-if="isMobile" :src="logoMobileSrc" class="logo" alt="Beta Logo" loading="eager" fetchpriority="high"
          width="150" height="auto" />
        <img v-else :src="logoSrc" class="logo" alt="Beta Logo" loading="eager" fetchpriority="high" width="180"
          height="auto" />

        <h2 class="login-title">Iniciar sesión</h2>

        <!-- Envolver en un formulario para mejorar la detección de autocompletado -->
        <form @submit.prevent="login" autocomplete="on">
          <div class="input-group">
            <CustomInputText id="username" v-model="usuario" label="Usuario" class="input no-zoom"
              @input="filtrarUsuario" autocomplete="username" name="username" type="text" />
          </div>

          <div class="input-group password-group">
            <CustomInputText v-model="password" :type="showPassword ? 'text' : 'password'" label="Contraseña"
              :feedback="false" class="input no-zoom" inputClass="w-full" @input="filtrarPassword"
              autocomplete="current-password" name="password" />
            <button type="button" class="password-toggle" @click="togglePasswordVisibility">
              <i :class="['pi', showPassword ? 'pi-eye-slash' : 'pi-eye']"></i>
            </button>
          </div>

          <button type="submit" class="login-button">Entrar</button>
        </form>

        <div v-if="datosUsuario.statusText === 'No Content' || datosUsuario.status === 204">
          <h5 class="error-message">{{ mensaje }}</h5>
        </div>

        <div class="switchdiv">
          <div class="toggle-label">Dark Mode</div>
          <label class="switch">
            <input type="checkbox" v-model="isDarkMode" @change="toggleDarkMode">
            <span class="slider round"></span>
          </label>

        </div>

        <!-- Version display -->
        <!-- <VersionDisplay /> -->
      </div>

      <!-- Carga diferida del panel promocional para mejorar el rendimiento inicial -->
      <div class="promo-panel" v-show="isPanelVisible">
        <div class="promo-content">
          <div class="motivational-phrase">
            <p class="phrase-line-1">{{ currentPhrase.line1 }}</p>
            <p class="phrase-line-2">{{ currentPhrase.line2 }}</p>
            <p class="phrase-line-3">{{ currentPhrase.line3 }}</p>
            <p class="phrase-author">{{ currentPhrase.author }}</p>
          </div>
        </div>
      </div>
      <CustomToast ref="customToast" />
    </div>
    <!-- El footer se maneja desde App.vue -->
    <AppFooter :isLogin="true" />
  </div>
</template>

<script>
import EmpleadosServicio from '../service/EmpleadosServicio';
import MenuService from '../service/MenuService';
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import CustomToast from '../components/ui/CustomToast.vue';
import CustomInputText from '../components/ui/CustomInputText.vue';
import VersionDisplay from '../components/ui/VersionDisplay.vue';
import AppFooter from '../components/Footer.vue';
// Importar las imágenes directamente
import betaD from '../assets/layout/betaD.webp';
import betaL from '../assets/layout/betaL.webp';
// Versiones más pequeñas optimizadas para móvil
import betaDMobile from '../assets/layout/betaD-mobile.webp';
import betaLMobile from '../assets/layout/betaL-mobile.webp';

export default {
  name: 'LoginPage',
  components: {
    CustomToast,
    CustomInputText,
    VersionDisplay,
    AppFooter
  },
  props: ['mensaje'],
  setup() {
    const isDarkMode = ref(false);
    const currentPhrase = ref({});
    const isPanelVisible = ref(false);
    const isMobile = ref(window.innerWidth <= 768);
    const windowWidth = ref(window.innerWidth);
    const syncViewportState = () => {
      windowWidth.value = window.innerWidth;
      isMobile.value = windowWidth.value <= 768;
    };
    // Variable para mostrar/ocultar información de depuración
    const showDebug = ref(true); // Cambia a false en producción

    // Collection of motivational phrases
    const motivationalPhrases = [
      {
        line1: "EL ÉXITO NO ES FINAL",
        line2: "EL FRACASO NO ES FATAL",
        line3: "ES EL CORAJE DE CONTINUAR LO QUE CUENTA",
        author: "Winston Churchill"
      },
      {
        line1: "LA DISCIPLINA ES EL PUENTE",
        line2: "ENTRE METAS Y LOGROS",
        line3: "CONSTRUYE TU CAMINO",
        author: "Jim Rohn"
      },
      {
        line1: "NO CUENTES LOS DÍAS",
        line2: "HAZ QUE LOS DÍAS CUENTEN",
        line3: "CADA MOMENTO ES UNA OPORTUNIDAD",
        author: "Muhammad Ali"
      },
      {
        line1: "LOS SUEÑOS NO FUNCIONAN",
        line2: "A MENOS QUE TÚ TAMBIÉN LO HAGAS",
        line3: "TRABAJA DURO, SUEÑA GRANDE",
        author: "John C. Maxwell"
      },
      {
        line1: "TÚ ERES EL AUTOR",
        line2: "DE TU PROPIA HISTORIA",
        line3: "ESCRIBE UN GRAN CAPÍTULO HOY",
        author: "Robin Sharma"
      },
      {
        line1: "EL LÍMITE ES SÓLO",
        line2: "UNA CREACIÓN DE LA MENTE",
        line3: "ROMPE TUS BARRERAS",
        author: "Napoleon Hill"
      },
      {
        line1: "NO HAY ASCENSOR",
        line2: "HACIA EL ÉXITO",
        line3: "HAY QUE TOMAR LAS ESCALERAS",
        author: "Zig Ziglar"
      },
      {
        line1: "LA PERSISTENCIA",
        line2: "PUEDE CAMBIAR EL FRACASO",
        line3: "EN UN LOGRO EXTRAORDINARIO",
        author: "Matt Biondi"
      },
      {
        line1: "NUNCA ES DEMASIADO TARDE",
        line2: "PARA SER LO QUE",
        line3: "PODRÍAS HABER SIDO",
        author: "George Eliot"
      },
      {
        line1: "LA MEJOR MANERA DE PREDECIR",
        line2: "EL FUTURO",
        line3: "ES CREÁNDOLO",
        author: "Peter Drucker"
      }
    ];

    // Pick a random phrase on component mount
    onMounted(() => {
      const savedDarkMode = sessionStorage.getItem('darkMode');
      if (savedDarkMode) {
        isDarkMode.value = savedDarkMode === 'true';
        applyTheme(isDarkMode.value);
      }

      // Select a random phrase
      const randomIndex = Math.floor(Math.random() * motivationalPhrases.length);
      currentPhrase.value = motivationalPhrases[randomIndex];

      // Mostrar panel promocional después de cargar elementos críticos
      setTimeout(() => {
        isPanelVisible.value = true;
      }, 100);

      // Detectar cambios en el tamaño de la ventana para ajuste responsivo
      window.addEventListener('resize', syncViewportState);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', syncViewportState);
    });

    const applyTheme = (isDark) => {
      if (isDark) {
        document.body.classList.add('dark');
        document.body.classList.remove('light-theme');
        document.body.classList.add('dark-theme');
      } else {
        document.body.classList.remove('dark');
        document.body.classList.add('light-theme');
        document.body.classList.remove('dark-theme');
      }
    };

    const toggleDarkMode = () => {
      sessionStorage.setItem('darkMode', isDarkMode.value);
      applyTheme(isDarkMode.value);
    };

    // Watch for changes to isDarkMode to ensure theme is applied
    watch(isDarkMode, (newValue) => {
      applyTheme(newValue);
    });

    // Añadir log para depuración de imágenes
    watch(isMobile, (newValue) => {

    });

    const logoSrc = computed(() => {
      const src = isDarkMode.value ? betaD : betaL;

      return src;
    });

    const logoMobileSrc = computed(() => {
      const src = isDarkMode.value ? betaDMobile : betaLMobile;

      return src;
    });

    return {
      isDarkMode,
      logoSrc,
      logoMobileSrc,
      toggleDarkMode,
      currentPhrase,
      isPanelVisible,
      isMobile,
      windowWidth,
      showDebug
    };
  },

  data() {
    return {
      usuario: '',
      password: '',
      datosUsuario: {},
      showPassword: false
    };
  },
  created() {
    // Si hay un puesto guardado, lo cargamos
    if (localStorage.getItem('puesto')) {
      this.rh = localStorage.getItem('puesto');
    }
  },
  methods: {
    login() {
      try {
        // Validar que el usuario y contraseña no estén vacíos
        if (!this.usuario || !this.password) {
          this.$refs.customToast.add({
            severity: 'error',
            summary: 'Datos incompletos',
            detail: 'Por favor ingrese usuario y contraseña',
            life: 3000
          });
          return; // Detener la ejecución si faltan datos
        }

        localStorage.setItem('usuario', this.usuario);
        const empleadoServicio = new EmpleadosServicio();
        const menuService = new MenuService();

        empleadoServicio.login(this.usuario, this.password).then(data => {
          this.datosUsuario = data;

          if (this.datosUsuario.data && this.datosUsuario.data.id != null) {
            // Verificar acceso al menú antes de permitir el login
            menuService.getMenuByUsuario(this.usuario).then(menuData => {
              // Verificar si el usuario tiene elementos de menú
              if (!menuData || menuData.length === 0) {
                this.$refs.customToast.add({
                  severity: 'warn',
                  summary: 'Sin Acceso',
                  detail: 'No tienes acceso a ningún elemento del menú. Por favor, contacta al administrador del sistema para solicitar los permisos necesarios.',
                  life: 5000
                });
                return; // No permitir el login
              }

              // Si tiene acceso, proceder con el login normal
              localStorage.setItem('tienda', this.datosUsuario.data.tiendaSucursal);
              localStorage.setItem('puesto', this.datosUsuario.data.puesto);
              // Guardar el valor del puesto en la variable rh
              this.rh = this.datosUsuario.data.puesto;

              // Extraer y guardar todas las acciones RBAC del usuario en localStorage
              const todasAcciones = new Set();
              menuData.forEach(menu => {
                (menu.subMenu || []).forEach(sub => {
                  (sub.acciones || []).forEach(a => todasAcciones.add(a));
                });
              });
              localStorage.setItem('acciones', JSON.stringify([...todasAcciones]));
              window.dispatchEvent(new Event('rbac-acciones-updated'));

              for (let i = 0; i < this.datosUsuario.data.tiendas.length; i++) {
                localStorage.setItem('permiso' + i, this.datosUsuario.data.tiendas[i].id);
                localStorage.setItem('determinante' + i, this.datosUsuario.data.tiendas[i].determinante);
                localStorage.setItem('sucursal' + i, this.datosUsuario.data.tiendas[i].formato + " " + this.datosUsuario.data.tiendas[i].sucursal);
                localStorage.setItem('permisos', i + 1);
              }
              this.$emit('login-success', this.datosUsuario);
            }).catch(menuError => {
              // En caso de error al verificar el menú, mostrar mensaje y no permitir login
              this.$refs.customToast.add({
                severity: 'warn',
                summary: 'Sin Acceso',
                detail: 'No se pudo verificar tu acceso al sistema. Por favor, contacta al administrador del sistema.',
                life: 5000
              });
            });
          } else {
            // Handle the case where data is undefined or does not contain the expected properties
            this.$refs.customToast.add({
              severity: 'error',
              summary: 'Login failed',
              detail: 'Usuario o contraseña inválidos',
              life: 3000
            });

          }
        }).catch(error => {
          // Handle the error case
          this.$refs.customToast.add({
            severity: 'error',
            summary: 'Login failed',
            detail: 'Usuario o contraseña inválidos',
            life: 3000
          });

        });
      } catch (error) {

      }
    },

    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },

    filtrarUsuario(event) {
      // Validar para permitir solo A-Z y 0-9
      if (this.usuario !== null && this.usuario !== undefined) {
        this.usuario = this.usuario.replace(/[^A-Z0-9_\-@]/g, '');
      } else {
        // Si es null o undefined, asignar cadena vacía
        this.usuario = '';
      }
    },

    filtrarPassword(event) {
      // Validar para permitir solo A-Z y 0-9
      if (this.password !== null && this.password !== undefined) {
        this.password = this.password.replace(/[^A-Z0-9_\-@]/g, '');
      } else {
        // Si es null o undefined, asignar cadena vacía
        this.password = '';
      }
    }
  }
}
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: 100dvh;
  background-color: var(--card-background-color);
  transition: background-color 0.3s ease;
  position: relative;
  box-sizing: border-box;
  overflow-y: auto;
  padding: clamp(1rem, 2vw, 1.5rem);
  padding-bottom: 4.5rem;
  /* Espacio para el footer */
}

.login-container_card {
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 900px;
  min-height: min(560px, calc(100dvh - 8rem));
  max-height: none;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-md);
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
  font-family: var(--font-body);
}

.login-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: clamp(1.5rem, 3vw, 2rem);
  background-color: var(--background-color);
  transition: background-color 0.3s ease;
}

.login-title {
  font-size: 1.5rem;
  margin: 1rem 0;
  color: var(--text-color);
  text-align: center;
  transition: color 0.3s ease;
}

.logo {
  width: 180px;
  height: auto;
  margin-bottom: 1rem;
  transition: filter 0.3s ease;
}

.input-group {
  width: 100%;
  max-width: 300px;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.password-group {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 10px;
  top: 70%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color);
  z-index: 2;
  padding: 0.5rem;
}

.password-toggle:focus {
  outline: none;
}

.input {
  width: 100%;
  color: var(--text-color);
}

.no-zoom {
  touch-action: manipulation;
  -ms-touch-action: manipulation;
  /* -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none; */
  font-size: 16px;
}

.input-help-text {
  width: 100%;
  text-align: left;
  font-size: 0.8rem;
  color: var(--text-secondary-color, #6c757d);
  margin-top: 0.3rem;
  padding-left: 0.2rem;
}

.login-button {
  width: 100%;
  max-width: 300px;
  background-color: var(--primary-color);
  border: none;
  color: var(--primary-text-color);
  padding: 0.75rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: background-color 0.3s;
}

.login-button:hover {
  background-color: var(--primary-hover-color);
  box-shadow: var(--shadow-sm);
}

.recover-password {
  margin-top: 1rem;
  text-align: center;
  width: 100%;
  max-width: 300px;
}

.recover-password a {
  color: var(--primary-color);
  text-decoration: none;
  font-size: 0.9rem;
}

.recover-password a:hover {
  text-decoration: underline;
}

.error-message {
  color: var(--danger-color);
  margin-top: 1rem;
  text-align: center;
}

.promo-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #171a1f 0%, #171a1f 100%);
  color: var(--primary-text-color);
  padding: clamp(1.5rem, 3vw, 2rem);
  min-height: 0;
}

.promo-content {
  max-width: 80%;
  text-align: center;
}

.motivational-phrase {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.switchdiv {
  display: flex;
  margin-top: 30px;
  align-items: center;
  gap: 13px;
  justify-content: center;
}

.switch {
  position: relative;
  display: inline-block;
  width: 34px;
  height: 20px;
  flex-shrink: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 12px;
  width: 12px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked+.slider {
  background-color: #2196F3;
}

input:checked+.slider:before {
  transform: translateX(14px);
}

.fixed-icon-size {
  width: 1.2em;
  height: 1.2em;
  vertical-align: 0em;
}

.phrase-line-1 {
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0.5rem 0;
  color: white;
  text-transform: uppercase;
}

.phrase-line-2 {
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0.5rem 0;
  color: var(--primary-color);
  text-transform: uppercase;
}

.phrase-line-3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0.5rem 0;
  color: white;
  text-transform: uppercase;
}

.phrase-author {
  font-family: 'Satisfy', cursive, sans-serif;
  font-size: 1.5rem;
  margin-top: 2rem;
  color: #e0e0e0;
  font-style: italic;
}

/* Custom styles for PrimeVue components */

form {
  width: 100%;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

@media (max-height: 820px) {
  .login-container {
    align-items: flex-start;
  }

  .login-container_card {
    min-height: auto;
  }
}

/* Responsive design for mobile */
@media (max-width: 768px) {
  .login-container_card {
    flex-direction: column;
    max-height: none;
    max-width: 95%;
    margin: 1rem;
    will-change: transform;
    /* Optimización para renderizado */
  }

  .logo {
    width: 120px !important;
    /* Forzar tamaño más pequeño */
    height: auto;
    max-height: 60px;
    /* Optimize rendering */
    transform: translateZ(0);
    backface-visibility: hidden;
  }

  .login-card {
    padding: 1.5rem;
  }

  .promo-panel {
    padding: 1rem;
    max-height: 300px;
  }

  .phrase-line-1,
  .phrase-line-2 {
    font-size: 1.4rem;
  }

  .phrase-line-3 {
    font-size: 1.2rem;
  }

  .phrase-author {
    font-size: 1.2rem;
    margin-top: 1rem;
  }

  .login-container {
    align-items: flex-start;
    padding-bottom: 5rem;
    /* Más espacio para el footer en móviles */
  }
}

@media (max-width: 768px) and (max-height: 760px) {
  .promo-panel {
    padding: 1rem;
  }

  .phrase-line-1,
  .phrase-line-2,
  .phrase-line-3 {
    margin: 0.3rem 0;
  }
}

/* Estilo para el panel de depuración */
.debug-mode {
  position: fixed;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px;
  z-index: 9999;
  font-size: 12px;
  border-radius: 0 0 5px 0;
}
</style>
